import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Card, CardContent, Chip, Divider,
  LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PersonIcon from '@mui/icons-material/Person';

import {
  fetchRequest as fetchTimeline,
  clearState as clearTimeline,
} from '../../redux/actions/discovery/goalTimeline';

// Each event type: a plain label + a one-line explanation of what happened,
// so a row reads clearly even when the backend sends no extra reason.
const ACTION_META = {
  retrain_requested: {
    label: 'Retraining started', color: 'info',
    desc: 'The data shifted, so the best model is being retrained.',
  },
  champion_promoted: {
    label: 'New model deployed', color: 'success',
    desc: 'A stronger model was promoted and is now serving.',
  },
  champion_fallback: {
    label: 'Switched to next-best model', color: 'info',
    desc: 'The top model couldn\u2019t be trained for serving, so the next-best one is being trained instead.',
  },
  promotion_ready: {
    label: 'New model ready', color: 'info',
    desc: 'A stronger model is ready for you to deploy.',
  },
  promotion_rejected: {
    label: 'Kept current model', color: 'default',
    desc: 'A candidate didn\u2019t beat the live model, so nothing changed.',
  },
  rediscovery_enqueued: {
    label: 'Re-discovery started', color: 'info',
    desc: 'Searched your signals again for new or changed drivers.',
  },
  revalidation_enqueued: {
    label: 'Drivers re-checked', color: 'default',
    desc: 'Re-checked every driver on fresh data to confirm it still holds.',
  },
  discovery_auto_enqueued: {
    label: 'New signals — discovery started', color: 'info',
    desc: 'New signals showed promise in a quick pre-check, so a full discovery run started.',
  },
  discovery_scheduled_enqueued: {
    label: 'Scheduled discovery started', color: 'info',
    desc: 'A full discovery run started on your chosen schedule.',
  },
  delta_screen_no_signal: {
    label: 'New signals checked — no signal', color: 'default',
    desc: 'New signals were pre-checked against your target; none showed promise, so no paid run started.',
  },
  serve_requested: {
    label: 'Forecast requested', color: 'default',
    desc: 'A fresh forecast was produced on demand.',
  },
  shock_entered: {
    label: 'Shock detected', color: 'warning',
    desc: 'A large event hit — forecasts widened their uncertainty.',
  },
  autopilot_paused: {
    label: 'Automation paused', color: 'warning',
    desc: 'Automated actions were paused.',
  },
  autopilot_mode_changed: {
    label: 'Automation mode changed', color: 'default',
    desc: 'The automation mode was updated.',
  },
};

const safeRelative = (d) => {
  try { if (!d) return ''; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return ''; }
};

// Legacy rows shipped with "(never billed)" baked into the reason — strip it.
const cleanReason = (r) => (r ? String(r).replace(/\s*\(never billed\)\s*/gi, '').trim() : '');

/**
 * The goal's activity log: every automatic and manual action — retrains,
 * deployments, re-discoveries, driver re-checks and shocks — with what
 * triggered it and what it cost. Always on screen (not an accordion).
 */
const AutopilotTimelineCard = ({ goalId }) => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.discovery_goal_timeline);

  useEffect(() => {
    if (goalId) dispatch(fetchTimeline({ goalId }));
    return () => { dispatch(clearTimeline()); };
  }, [goalId, dispatch]);

  const data = state?.response;
  const autopilot = data?.autopilot || {};
  const rows = data?.timeline || [];
  const cap = Number(autopilot.monthly_spend_cap) || 0;
  const spent = Number(autopilot.month_spend) || 0;
  const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Activity log</Typography>
          {rows.length > 0 && (
            <Chip size="small" variant="outlined" label={rows.length} sx={{ height: 20 }} />
          )}
        </Stack>

        <Stack spacing={1.5}>
          {cap > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Automation budget this month
                </Typography>
                <Typography variant="caption" color={pct >= 100 ? 'warning.main' : 'text.secondary'}>
                  {spent} / {cap} credits
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={pct >= 100 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {state?.isLoading && !data && <LinearProgress />}

          {!state?.isLoading && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nothing yet — automatic retrains, deployments, re-discoveries and
              driver re-checks will appear here with what triggered them.
            </Typography>
          )}

          {rows.length > 0 && <Divider />}

          <Stack spacing={1}>
            {rows.map((row, i) => {
              const meta = ACTION_META[row.action]
                || { label: row.action, color: 'default', desc: '' };
              const byUser = row.actor === 'user';
              const reason = cleanReason(row.reason) || meta.desc;
              const charged = Number(row.charge) > 0;
              return (
                <Stack
                  key={`${row.ts}-${i}`}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{ pb: 1, borderBottom: i < rows.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <Tooltip title={byUser ? 'You did this' : 'Done automatically'} arrow>
                    {byUser
                      ? <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.35 }} />
                      : <AutoModeIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.35 }} />}
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" color={meta.color} variant="outlined" label={meta.label} />
                      {charged ? (
                        <Chip size="small" variant="outlined" label={`${row.charge} credits`} />
                      ) : (
                        <Tooltip title="This automatic action didn't use any credits" arrow>
                          <Chip size="small" variant="outlined" label="No credits" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {safeRelative(row.ts)}
                      </Typography>
                    </Stack>
                    {reason && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {reason}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AutopilotTimelineCard;
